<template>
  <Modal
    ref="galleryCopyModal"
    :title="copyModalTitle"
  >
    <template #modal-main>
      <form
        id="gallery-copy-form"
        class="form container scrollbar"
        novalidate
        @submit.prevent=""
      >
        <div class="copy-card">
          <!-- Source Annotation Set Section -->
          <h3>Source - Annotation Set</h3>
          <div class="row">
            <div class="col-6 settings__control-group">
              <label class="required">Annotation Set</label>
              <select
                v-model="sourceAnnset"
                class="select"
                required
              >
                <option :value="null">None</option>
                <option
                  v-for="annSet in sourceAnnotationSets"
                  :key="`src-annset-${annSet.id}`"
                  :value="annSet"
                >
                  {{ annSet.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Files List -->
          <hr class="mt-3">
          <h3>{{ copyType }} ({{ selectedImages?.length || 0 }})</h3>
          <div class="row">
            <div v-if="selectedImages && selectedImages.length > 0" class="col file-grid scrollbar">
              <div v-for="(img, i) in selectedImages" :key="`file-${i}`" class="file-grid__cell">
                <span>{{ img.name }}</span>
              </div>
            </div>
            <div v-else class="col text-muted">
              No {{ copyType.toLowerCase() }} selected
            </div>
          </div>

          <!-- Destination Section -->
          <hr class="mt-3">
          <h3>Destination</h3>
          <div class="row">
            <div class="col-6 settings__control-group">
              <label class="required">Project</label>
              <select
                v-model="destProject"
                class="select"
                required
                @change="handleDestProjectChange"
              >
                <option :value="null">Select a Project</option>
                <option
                  v-for="project in destProjectList"
                  :key="`dest-project-${project.id}`"
                  :value="project"
                >
                  {{ project.name }}
                </option>
              </select>
            </div>
            <div class="col-6 settings__control-group">
              <label class="required">Dataset</label>
              <select
                v-model="destDataset"
                class="select"
                required
                @change="handleDestDatasetChange"
              >
                <option :value="null">New Dataset</option>
                <option
                  v-for="dataset in destDatasets"
                  :key="`dest-dataset-${dataset.id}`"
                  :value="dataset"
                >
                  {{ dataset.name }}
                </option>
              </select>
            </div>
            <div v-if="!destDataset" class="col-6 settings__control-group">
              <label>New Dataset Name</label>
              <input
                v-model="newDatasetName"
                type="text"
                class="select"
                :placeholder="source ? `Copy of ${source.name}` : 'Enter new dataset name'"
              />
            </div>
          </div>

          <!-- Destination Annotation Set -->
          <div v-if="destDataset && destDataset.annotation_sets && destDataset.annotation_sets.length > 0" class="row mt-2">
            <div class="col-6 settings__control-group">
              <label class="required">Destination Annotation Set</label>
              <select
                v-model="destAnnset"
                class="select"
                required
              >
                <option :value="null">New Annotation Set</option>
                <option
                  v-for="annSet in destDataset.annotation_sets"
                  :key="`dest-annset-${annSet.id}`"
                  :value="annSet"
                >
                  {{ annSet.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Copy Predefined Annotations Option -->
          <div v-if="!destDataset" class="row mt-2">
            <div class="col-12">
              <v-checkbox
                v-model="copyPredefinedAnnotations"
                color="#250e81"
                :hide-details="true"
                :density="'compact'"
              >
                <template #label>
                  <span class="checkbox-text-label">Copy All Annotation Sets from Source Dataset</span>
                </template>
              </v-checkbox>
            </div>
          </div>

          <!-- Status Messages -->
          <div v-if="message" class="row mb-3 result-container mt-3">
            <div class="result" :class="[isLoading ? 'info' : (success ? 'success' : 'error')]">
              <span v-if="isLoading" class="loader"></span>
              <span><pre>{{ message }}</pre></span>
            </div>
          </div>
        </div>
      </form>
    </template>
    <template #modal-footer>
      <div class="footer">
        <button
          class="button modal-action-button"
          :disabled="isLoading || !destProject || !selectedImages?.length"
          @click="cancel"
        >
          Cancel
        </button>
        <button
          class="button modal-action-button"
          :class="{'button-spinner': isLoading}"
          :disabled="isLoading || !destProject || !selectedImages?.length"
          @click="applyCopy"
        >
          Copy
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/Modal.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import { event } from 'vue-gtag';

const $toast = useToast();

export default {
  name: 'GalleryCopy',
  components: {
    Modal,
  },
  props: {
    source: {
      type: Object,
      default: () => ({}),
    },
    datasets: {
      type: Array,
      default: () => [],
    },
    selectedImages: {
      type: Array,
      default: null,
    },
    mode: {
      type: String,
      default: 'images',
      validator: (value) => ['images', 'sequences'].includes(value),
    },
  },
  emits: [
    'copy-complete', 'copy-in-progress', 'closed',
  ],
  data() {
    return {
      dataConnect: null,
      sourceAnnset: null,
      destProject: null,
      destProjectList: [],
      destDatasets: [],
      destDataset: null,
      destAnnset: null,
      copyPredefinedAnnotations: false,
      isLoading: false,
      success: false,
      message: '',
      newDatasetName: '',
    };
  },
  computed: {
    sourceAnnotationSets() {
      return this.source?.annotation_sets || [];
    },
    username() {
      return this.$store.state.user?.user?.username || 'unknown';
    },
    projectList() {
      return this.$store.state.projects?.projectList || [];
    },
    images() {
      if (this.selectedImages) {
        return this.selectedImages
          .filter(obj => obj.frame_no > -1 || !obj.sequence_id)
          .map(obj => obj.id);
      }
      return [];
    },
    sequences() {
      if (this.selectedImages) {
        const seqIds = this.selectedImages
          .filter(obj => obj.frame_no === -1 && obj.sequence_id !== null)
          .map(obj => obj.sequence_id);
        return [...new Set(seqIds)];
      }
      return [];
    },
    copyType() {
      return this.mode === 'sequences' ? 'Sequences' : 'Images';
    },
    copyModalTitle() {
      return `Copy ${this.copyType}`;
    },
  },
  created() {
    this.dataConnect = new DatastoreConnect(this.$store.state.enterpriseServerUrl);
  },
  async mounted() {
    await this.loadWritableProjects();
  },
  methods: {
    async loadWritableProjects() {
      try {
        const data = await this.dataConnect.getProjectList({ only_writable: true });
        if (data.error) throw data.error;
        this.destProjectList = data.result || [];
      } catch (error) {
        console.error('Error loading projects:', error);
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Failed to load projects',
          type: 'error',
          position: 'top',
        });
      }
    },
    async handleDestProjectChange() {
      if (this.destProject) {
        this.destDataset = null;
        this.destAnnset = null;
        this.newDatasetName = '';
        await this.loadDatasets(this.destProject);
      }
    },
    async loadDatasets(project) {
      try {
        const params = {
          project_id: project.id,
          get_annotation_sets: true,
          get_labels: true,
          get_groups: true,
        };
        const resp = await this.dataConnect.getDatasetList(params);
        if (resp.error) throw resp.error;
        this.destDatasets = resp.result || [];
      } catch (error) {
        console.error('Error loading datasets:', error);
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Failed to load datasets',
          type: 'error',
          position: 'top',
        });
      }
    },
    async handleDestDatasetChange() {
      if (this.destDataset) {
        // Reset annotation set selection when dataset changes
        this.destAnnset = null;
      }
    },
    showModal() {
      this.$refs.galleryCopyModal.showModal();
    },
    closeModal() {
      this.$refs.galleryCopyModal.closeModal();
    },
    cancel() {
      this.clearForm();
      this.closeModal();
      this.$emit('closed');
    },
    clearForm() {
      this.sourceAnnset = null;
      this.destProject = null;
      this.destDataset = null;
      this.destAnnset = null;
      this.copyPredefinedAnnotations = false;
      this.newDatasetName = '';
      this.message = '';
      this.success = false;
      this.isLoading = false;
    },
    async applyCopy() {
      // Validation
      if (!this.destProject) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Please select a destination project',
          type: 'error',
          position: 'top',
        });
        return;
      }

      if (!this.selectedImages || this.selectedImages.length === 0) {
        $toast.open({
          queue: true,
          duration: 5000,
          message: 'No files selected',
          type: 'error',
          position: 'top',
        });
        return;
      }

      this.isLoading = true;
      this.message = 'Copying files...';

      try {
        const params = {
          dataset_id: this.source.id,
          dest_project_id: this.destProject.id,
          dest_dataset_id: this.destDataset?.id || null,
          source_annotation_set_id: this.sourceAnnset?.id || null,
          dest_annotation_set_id: this.destAnnset?.id || null,
          copy_all_annotation_sets: this.copyPredefinedAnnotations && !this.destDataset,
          images: this.images,
          sequences: this.sequences,
        };

        if (!this.destDataset && this.newDatasetName) {
          params.dest_dataset_name = this.newDatasetName;
        }

        const resp = await this.dataConnect.copySequences(params);

        if (resp.error) {
          throw resp.error;
        }

        this.isLoading = false;
        this.success = true;
        this.message = 'Copy operation started successfully!';

        $toast.open({
          queue: true,
          duration: 5000,
          message: 'Files copied successfully',
          type: 'success',
          position: 'top',
        });

        event('gallery_copy_success', {
          category: 'gallery_copy',
          label: 'User successfully copied files from gallery',
          user: this.username,
        });

        this.$emit('copy-in-progress');
        setTimeout(() => {
          this.clearForm();
          this.closeModal();
        }, 1500);
      } catch (error) {
        this.isLoading = false;
        this.success = false;
        this.message = `Error: ${error.message || 'Copy failed'}`;

        event('gallery_copy_failure', {
          category: 'gallery_copy',
          label: error.message || 'Copy failed',
          user: this.username,
        });

        $toast.open({
          queue: true,
          duration: 5000,
          message: error.message || 'Copy failed',
          type: 'error',
          position: 'top',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';

#gallery-copy-form {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 0 2rem;
  margin: 0.75rem 0;
  overflow: auto;
  position: relative;
}

@media (min-width: $breakpoint-sm) {
  #gallery-copy-form {
    width: 700px;
  }
}

.copy-card {
  display: flex;
  flex-direction: column;
  background: var(--color-white-100);
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--color-primary-100);
}

.settings__control-group {
  align-items: flex-start;
  min-width: 200px;

  label {
    display: block;
    margin-bottom: 4px;
    font-weight: 600;
    font-size: 14px;

    &.required::after {
      content: ' *';
      color: red;
    }
  }
}

.select {
  width: 100%;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid var(--color-primary-200);
  font-size: 14px;
  background-color: var(--color-white-100);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--color-primary-500);
    box-shadow: 0 0 0 3px rgba(37, 14, 129, 0.1);
  }
}

.file-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  width: 100%;
  flex: 1 1 auto;
  gap: 10px;
  padding: 10px;
  overflow-y: auto;
  max-height: 250px;
  border-radius: 4px;
  background-color: var(--color-white-800);

  &__cell {
    display: flex;
    flex-direction: row;
    font-size: 13px;
    padding: 8px;
    height: auto;
    border-radius: 4px;
    background-color: var(--color-white-100);
    border: 1px solid var(--color-primary-100);
    align-items: center;
    justify-content: space-between;
    word-break: break-word;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      word-break: break-word;
    }
  }
}

.text-muted {
  color: var(--color-text-secondary);
  font-size: 14px;
  padding: 20px;
  text-align: center;
}

.result-container {
  justify-content: center;
}

.result {
  display: flex;
  width: 100%;
  border-radius: 5px;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 10px 15px;
  align-items: center;
  justify-content: center;
  gap: 10px;

  pre {
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  &.success {
    background: rgba(115, 206, 115, 0.2);
    color: #2d7a2d;
    border: 1px solid #73ce73;
  }

  &.error {
    background: rgba(182, 94, 94, 0.2);
    color: #8b3a3a;
    border: 1px solid #b65e5e;
  }

  &.info {
    background: rgba(230, 221, 239, 0.3);
    color: var(--color-primary-500);
    border: 1px solid var(--color-primary-500);
  }
}

.loader {
  width: 20px;
  height: 20px;
  border: 3px solid var(--color-primary-200);
  border-top-color: var(--color-primary-500);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.checkbox-text-label {
  margin-left: 8px;
  font-size: 14px;
  font-weight: 500;
}

.footer {
  display: flex;
  flex-direction: row;
  padding: 1rem 2rem;
  justify-content: flex-end;
  gap: 10px;
}

.modal-action-button {
  min-width: 100px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
